import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Check, Search, X } from 'lucide-react';
import { Input } from '../ui/input';

// Pick one or several workers for a job (Sep 2026). `value` = array of user ids.
// Open-job counts per worker (admins/supervisors) so dispatchers can balance the load.
const useWorkload = () => {
  const [wl, setWl] = useState({});
  useEffect(() => {
    axios.get(`${window.__BACKEND_URL__}/api/users/workload`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then((r) => setWl(r.data.workload || {})).catch(() => setWl({}));
  }, []);
  return wl;
};

const loadTone = (n) => (n >= 5 ? 'text-red-300 bg-red-500/15 border-red-500/40' : n >= 3 ? 'text-amber-300 bg-amber-500/15 border-amber-500/40' : 'text-slate-300 bg-slate-600/30 border-slate-600');

export default function WorkerMultiSelect({ users = [], value = [], onChange, testId = 'worker-multiselect' }) {
  const [q, setQ] = useState('');
  const workload = useWorkload();
  const selected = useMemo(() => users.filter((u) => value.includes(u.id)), [users, value]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    return users.filter((u) => !s || (u.name || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s) || (u.role || '').toLowerCase().includes(s));
  }, [users, q]);
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="space-y-2" data-testid={testId}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid={`${testId}-chips`}>
          {selected.map((u) => (
            <span key={u.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-[#00C896]/15 text-[#00C896] border border-[#00C896]/40" data-testid={`${testId}-chip-${u.id}`}>
              {u.name}
              <button type="button" onClick={() => toggle(u.id)} className="hover:text-white" aria-label={`Remove ${u.name}`} data-testid={`${testId}-remove-${u.id}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-2.5 top-3 text-slate-500" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workers by name, email or role…" className="h-10 pl-8" data-testid={`${testId}-search`} />
      </div>
      <div className="max-h-44 overflow-y-auto rounded-lg border border-[#2D3748] bg-[#0F1923]" role="listbox" aria-multiselectable="true">
        {list.length === 0 ? <p className="text-xs text-slate-500 px-3 py-2">No workers match.</p> : list.map((u) => {
          const on = value.includes(u.id);
          return (
            <button type="button" key={u.id} role="option" aria-selected={on} onClick={() => toggle(u.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm border-b border-[#2D3748]/60 last:border-b-0 hover:bg-white/5 ${on ? 'text-white' : 'text-slate-300'}`}
              data-testid={`${testId}-option-${u.id}`}>
              <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-[#00C896] border-[#00C896]' : 'border-slate-500'}`}>{on && <Check className="w-3 h-3 text-[#0F1923]" />}</span>
              <span className="truncate">{u.name}</span>
              {(() => { const w = workload[u.id]; const n = w?.open || 0; return (
                <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${loadTone(n)}`} title={w ? `${w.in_progress || 0} in progress · ${w.paused || 0} paused · ${w.to_do || 0} to do` : 'No open jobs'} data-testid={`${testId}-load-${u.id}`}>{n} open</span>
              ); })()}
              <span className="ml-auto text-xs text-slate-500 shrink-0">{u.role}{u.user_type ? ` · ${u.user_type}` : ''}</span>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500" data-testid={`${testId}-count`}>{selected.length === 0 ? 'Pick one or more workers.' : `${selected.length} worker${selected.length === 1 ? '' : 's'} selected — each gets the job in their own list and is notified.`}</p>
    </div>
  );
}

// "Team · Parent › Squad" labels for the team dropdown.
export const teamLabel = (t) => (t.is_subteam || t.parent_team_id) ? `Squad · ${t.parent_team_name ? `${t.parent_team_name} › ` : ''}${t.name}` : `Team · ${t.name}${t.location ? ` — ${t.location}` : ''}`;
